import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { CustomerContext } from '../../contexts/CustomerContext';
import useQueryString from '../../hooks/use-query-string';
import { School, SchoolType } from '../../models/_models';
import { Student } from '../../models';
import routes from '../../routes';
import styles from '../_pages/EarlyPurchase/EarlyPurchase.module.scss';
import Typography from '../Typography';
import TextInput from '../TextInput';
import GradeInput from '../GradeInput';
import Button from '../Button';
import StudentImage from '../../models/StudentImage';
import useLocalStorage from '../../hooks/useLocalStorage';
import SchoolInput from '../SchoolInput';
import useSchools from '../../hooks/useSchools';
import { isValidEmail } from './AccessCodeOnlyForm';
import { ToastContainer, toast } from 'react-toastify';

const NOT_SUBMITTED = 'NOT_SUBMITTED'
const USED_ACCESS_CODE = 'USED_ACCESS_CODE'
const USED_STUDENT_ID = 'USED_STUDENT_ID'
const USED_STUDENT_INFO = 'USED_STUDENT_INFO'

/*
Three branches, checked in this order:
- Staff order (teacher === 'Staff'): staff have no Student ID, so match by
  name against the school's staff roster via the backend
  match_or_create_staff endpoint (TeacherData::StaffMatcher), never the
  generic name search. Checked BEFORE no-student-data so a staff order at a
  no-student-data school still gets tagged grade: 'ST' and matched via the
  shared matcher, instead of silently falling through to findStudentByInfo
  (which wouldn't force grade 'ST' and would risk minting a duplicate
  Student row for the same staff member on a later visit).
- No-student-data school (currentSchool.noStudentData): this school has no
  roster/photo data to match against, so skip ID/roster matching entirely --
  first/last name only, findStudentByInfo()'s match-or-create is the
  INTENDED behavior here (there's no photo to mismatch either way).
- Default (has roster data, not staff): Student ID is MANDATORY. Try access
  code, then exact Student ID match. If it doesn't resolve to exactly one
  student, show an inline error -- do NOT fall back to a name search or
  silently create a student. (This used to fall through to a name search
  that silently created a blank Student record on a bad/ambiguous match --
  the "wrong kid" risk that findStudentByInfo's match-or-create is now
  scoped away from.)
 */

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#000' : '#777',
    fontFamily: 'inherit',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#777',
    fontFamily: 'inherit',
    fontSize: '0.95em',
  }),
}

export default function AnotherStudentForm() {
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const { order } = useQueryString()
  const [orderStatus, setOrderStatus] = useState(NOT_SUBMITTED)

  const { currentSchool } = customer

  const [studentId, setStudentId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [grade, setGrade] = useState('')
  const [teacher, setTeacher] = useState('')
  const [teachers, setTeachers] = useState([])
  const [isHighSchool, setIsHighSchool] = useState(false)
  const [isOrderingAhead, setIsOrderingAhead] = useLocalStorage('orderingAhead')
  const [isUsingAccessCode, setIsUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const [email, setEmail] = useState(customer.email || '');
  const [noEmail, setNoEmail] = useState(false);
  const [triedToContinueWithoutSchool, setTCWS] = useState(false);
  const [studentIdError, setStudentIdError] = useState(false);
  const schools = useSchools();

  const isNoStudentDataSchool = Boolean(currentSchool && currentSchool.noStudentData);
  const isStaffOrder = teacher === 'Staff';

  const handleSchoolChange = (opt) => {
    const newSchool = schools.find((school) => school.id === opt.value);

    setCustomer({ ...customer, currentSchool: newSchool });
  };

  const isYearbookOrder = order === 'yearbook_order'
  const isPanoramaOrder = order === 'panorama'

  const [submitting, setSubmitting] = useState(false)

  const findStudentByInfo = () => {
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      setSubmitting(false);
      return;
    }

    setCustomer({ ...customer, email });

    const queryParams = {
      firstName: { match: firstName },
      lastName: { match: lastName },
      schoolId: customer.currentSchool.id,
      isEnrolled: true,
      grade,
    }

    Student
      .where({ ...queryParams })
      .all()
      .then((res) => {
        // if there are too many or no results that match, then make a new student
        if (res.data.length > 1 || res.data.length === 0) {
          const newStudent = new Student({
            firstName,
            lastName,
            grade,
            teacher,
            schoolId: customer.currentSchool.id,
          })

          newStudent.save().then(() => {
            setCustomer({ ...customer, currentStudent: newStudent })
            setOrderStatus(USED_STUDENT_INFO)
          })
        } else {
          setCustomer({ ...customer, currentStudent: res.data[0] })
          setOrderStatus(USED_STUDENT_INFO)
        }
      })
      .finally(() => setTCWS(false));
  }

  // Staff bypass: match by name against the school's staff roster via the
  // blessed TeacherData::StaffMatcher (server-side) rather than the generic
  // substring name search -- staff orders skip Student ID entirely.
  const matchOrCreateStaff = () => {
    axios.post('/students/match_or_create_staff', {
      school_id: customer.currentSchool.id,
      first_name: firstName,
      last_name: lastName,
    }).then((res) => {
      const { id, student_id: sid } = res.data.data
      setCustomer({ ...customer, currentStudent: { id, studentId: sid } })
      setOrderStatus(USED_STUDENT_INFO)
    }).catch(() => {
      toast.error("We couldn't process this staff order. Please try again or contact your school.")
      setSubmitting(false)
    })
  }

  useEffect(() => {
    if (!customer.currentSchool) return;

    axios.get(`/students/teachers?school_id=${customer.currentSchool.id}`)
      .then((res) => {
        setTeachers(['Staff', ...new Set(res.data.data.map((teacher) => teacher.name))]);
      })
  }, [customer?.currentSchool])

  useEffect(() => {
    if (!customer.currentSchool) return;

    if (customer.currentSchool.schoolTypeId) {
      SchoolType
        .find(customer.currentSchool.schoolTypeId)
        .then((res) => {
          setIsHighSchool(res.data.name === 'High School')
        })
    } else {
      School
        .find(customer.currentSchool.id)
        .then((res) => {
          SchoolType
            .find(res.data.schoolTypeId)
            .then((schoolTypeRes) => {
              setIsHighSchool(schoolTypeRes.data.name === 'High School')
            })
        })
    }
  }, [customer?.currentSchool])

  const queryHandler = (e) => {
    e.preventDefault()

    if (!customer.currentSchool) {
      setTCWS(true);
      return;
    }

    if (!isHighSchool && !isPanoramaOrder && !isNoStudentDataSchool && teacher === '') {
      toast.error('Please select a teacher before continuing');
      return;
    }

    if ((isStaffOrder || isNoStudentDataSchool) && (!firstName.trim() || !lastName.trim())) {
      toast.error('Please enter first and last name before continuing');
      return;
    }

    // Mandatory Student ID is a yearbook-only rule (personalization/cover-photo
    // risk). Panorama keeps the old optional-ID, name-search behavior below.
    if (isYearbookOrder && !isNoStudentDataSchool && !isStaffOrder && !studentId) {
      setStudentIdError(true);
      return;
    }

    setStudentIdError(false)
    setSubmitting(true)

    if (isStaffOrder) {
      matchOrCreateStaff()
      return
    }

    if (isNoStudentDataSchool) {
      findStudentByInfo()
      return
    }

    const enrolledParam = order === 'yearbook_order' ? {} : { isEnrolled: true }

    if (!studentId) {
      findStudentByInfo()
      return
    }

    StudentImage
      .selectExtra(['packageId'])
      .includes('student')
      .where({ for_access_code_and_school: [studentId, currentSchool.id] })
      .all()
      .then((response) => {
        if (response.data.length === 1) {
          setCustomer({ ...customer, currentStudent: response.data[response.data.length - 1].student })
          setIsUsingAccessCode(true)
          setOrderStatus(USED_ACCESS_CODE)
          setSubmitting(false)
          return
        }

        Student
          .where({ studentId, schoolId: currentSchool.id, ...enrolledParam })
          .all()
          .then((res) => {
            if (res.data.length === 1) {
              setCustomer({ ...customer, currentStudent: res.data[res.data.length - 1] })
              setOrderStatus(USED_STUDENT_ID)
              return
            }

            if (isYearbookOrder) {
              setStudentIdError(true)
              setSubmitting(false)
            } else {
              findStudentByInfo()
            }
          })
      })
  }

  const studentIdBlurHandler = () => {
    const enrolledParam = order === 'yearbook_order' ? {} : { isEnrolled: true }

    if (!studentId) return
    if (!customer.currentSchool) return;

    StudentImage
      .selectExtra(['packageId'])
      .includes('student')
      .where({ for_access_code_and_school: [studentId, currentSchool.id] })
      .all()
      .then((response) => {
        if (response.data.length === 1) {
          const { student } = response.data[response.data.length - 1]
          setCustomer({ ...customer, currentStudent: response.data[response.data.length - 1].student })
          setIsUsingAccessCode(true)
          setOrderStatus(USED_ACCESS_CODE)
          setSubmitting(false)
          setFirstName(student.firstName)
          setLastName(student.lastName)
          setGrade(student.grade.toString())
          setTeacher(student.teacher)
          return
        }

        Student
          .where({ studentId, schoolId: currentSchool.id, ...enrolledParam })
          .all()
          .then((res) => {
            if (res.data.length === 1) {
              const student = res.data[res.data.length - 1]
              setCustomer({ ...customer, currentStudent: res.data[res.data.length - 1] })
              setOrderStatus(USED_STUDENT_ID)
              setFirstName(student.firstName)
              setLastName(student.lastName)
              setGrade(student.grade.toString())
              setTeacher(student.teacher)
            }
          })
      })
  }

  const continueHandler = () => {
    // setIsOrderingAhead(true)
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      setSubmitting(false);
      return;
    }

    setCustomer({ ...customer, email });

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }))
    } else if (isPanoramaOrder) {
      history.push(routes.panorama({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }));
    } else {
      history.push('package_selection')
    }
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      studentIdBlurHandler()
    }, 2000)

    return () => clearTimeout(delayDebounceFn)
  }, [studentId])

  useEffect(() => {
    if (!(customer && customer.currentStudent)) return
    if (orderStatus === NOT_SUBMITTED || orderStatus === USED_ACCESS_CODE) return

    // setIsOrderingAhead(true)
    if (!email || !isValidEmail(email)) {
      setNoEmail(true);
      setSubmitting(false);
      return;
    }

    setCustomer({ ...customer, email });

    if (orderStatus === USED_ACCESS_CODE) {
      localStorage.setItem('isUsingAccessCode', 'true')
    }
    // if (orderStatus === NOT_SUBMITTED ||
    //     ((orderStatus === USED_ACCESS_CODE || orderStatus === USED_STUDENT_ID) && order !== 'yearbook_order')
    // ) return

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }))
    } else if (isPanoramaOrder) {
      history.push(routes.panorama({ sid: customer.currentStudent.studentId, sch: customer.currentSchool.id }));
    } else {
      history.push('package_selection')
    }
  }, [orderStatus, customer && customer.currentStudent])

  const onSubmit = orderStatus === 'NOT_SUBMITTED' ? queryHandler : continueHandler

  let titleText;
  if (order === 'yearbook_order') {
    titleText = 'Yearbooks';
  } else if (order === 'panorama') {
    titleText = 'Panoramas';
  }

  return (
    <>
      <ToastContainer
        position="bottom-right"
      />
      {titleText && <h1 className={styles.title}>{`Purchase ${titleText}`}</h1>}
      {noEmail && (
        <p className={styles.noStudentMsg}>
          Please enter a valid email before continuing.
        </p>
      )}
      {triedToContinueWithoutSchool && (
        <p className={styles.noStudentMsg}>
          Please select a school before continuing.
        </p>
      )}
      <form onSubmit={onSubmit} className={styles.container}>
        <label style={{ width: '100%' }}>
          Email
          <TextInput val={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <div>
          <Typography weight="bold" align="center" classes={styles.shoobBlueText}>
            Please enter your student information to continue
          </Typography>
        </div>
        <div>
          <SchoolInput onChange={handleSchoolChange} />
        </div>
        {isStaffOrder && (
          <Typography align="center" classes={styles.shoobBlueText}>
            Staff Members — please continue without a Student ID Number.
          </Typography>
        )}
        {!isStaffOrder && (
          <TextInput
            fullWidth
            val={studentId}
            label={isNoStudentDataSchool || !isYearbookOrder ? 'Student ID (optional)' : 'Student ID'}
            error={studentIdError}
            onChange={(e) => { setStudentId(e.target.value); setStudentIdError(false) }}
            onBlur={studentIdBlurHandler}
          />
        )}
        {studentIdError && (
          <p className={styles.noStudentMsg}>
            We couldn&apos;t find a student with that ID at this school. Please double-check the number from your photo day flyer or email, or contact your school office.
          </p>
        )}
        <TextInput
          fullWidth
          val={firstName}
          label="First Name"
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextInput
          fullWidth
          val={lastName}
          label="Last Name"
          onChange={(e) => setLastName(e.target.value)}
        />
        <label>
          <label style={{ fontSize: '0.8em', marginLeft: '14px' }}>Grade</label>
          <GradeInput
            val={grade}
            onChange={(val) => setGrade(val)}
          />
        </label>
        {!isHighSchool && !isPanoramaOrder && (
          <>
            {teachers.length > 0 ? (
              <>
                <label style={{ fontSize: '0.8em', marginLeft: '14px', marginBottom: '-20px' }}>
                  Teacher&apos;s Name
                  {!isNoStudentDataSchool && <span style={{ color: 'red' }}>*</span>}
                </label>
                <Select
                  defaultValue={teachers[0]}
                  styles={customStyles}
                  options={teachers
                    .map((teacherName) => ({ value: teacherName, label: teacherName }))}
                  onChange={(e) => setTeacher(e.value)}
                />
              </>
            ) : (
              <TextInput
                fullWidth
                val={teacher}
                label="Teacher's Last Name"
                onChange={(e) => setTeacher(e.target.value)}
              />
            )}
          </>
        )}
        {orderStatus === USED_ACCESS_CODE && (
          <Typography align="center">
            By continuing, the user affirms that they are the legal guardian or close relative of the student whose image(s) they are requesting and that they authorize Shoob Photography to print and mail portraits of them to their home address.
          </Typography>
        )}
        <Button disabled={submitting} type="submit" fullWidth variant="submit">
          Continue
        </Button>
      </form>
    </>
  )
}
